import PropTypes from 'prop-types';
import * as React from 'react';
import { getSwanImageUrl } from '../../../core/utilities/manifest-images.utils.js';
import { AlertBoxContext } from './alert-box.context.js';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useSwanContext } from '../../contexts/swan-provider/swan-provider.context.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { RenderComp } from '../core/render-comp.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * The visual style of the AlertBox
   */ skin: PropTypes.oneOf([
        'standard',
        'warning',
        'error',
        'positive',
        'legal-warning'
    ]),
    /**
   * @deprecated use `width` prop instead
   */ widthVariant: deprecatedProp(PropTypes.oneOf([
        'standard',
        'narrow'
    ]), DEPRECATED_PROP_MESSAGES.width),
    /**
   * The width of the AlertBox
   *
   * @default "standard"
   */ width: PropTypes.oneOf([
        'standard',
        'narrow'
    ]),
    /**
   * Whether or not the alert is a toast
   */ toast: PropTypes.bool,
    /**
   * Whether or not the alert is presently dismissed
   */ dismissed: PropTypes.bool,
    /**
   * Callback fired when the user has requested that the alert be dismissed
   */ onRequestDismiss: PropTypes.func
};
const propKeysToRemove = Object.keys(propTypes);
const AlertBox = renderWithRef('AlertBox', propTypes, (props, ref)=>{
    useComponentStylesLoaded('AlertBox', SWAN_STYLE_KEY_MAP.alertBox);
    const { swanPathType, swanBaseUrl } = useSwanContext();
    const { skin = 'standard', widthVariant = 'standard', width = widthVariant, toast = false, children, dismissed: controlledDismissed, onRequestDismiss: controlledOnRequestDismiss } = props;
    const [dismissed, onRequestDismiss] = useControllableValue({
        value: controlledDismissed,
        onChange: controlledOnRequestDismiss || undefined,
        defaultValue: false,
        defaultOnChange: (setValue)=>{
            setValue(true);
        }
    });
    if (dismissed) {
        return null;
    }
    const classNames = new Set([
        'swan-vanilla-ignore',
        'swan-alert-box'
    ]);
    if (skin !== 'standard') classNames.add(`swan-alert-box-skin-${skin}`);
    if (width === 'narrow') classNames.add(`swan-alert-box-narrow`);
    if (toast) classNames.add(`swan-alert-box-toast`);
    const warningImgUrl = getSwanImageUrl('legalWarning', swanPathType, swanBaseUrl);
    return /*#__PURE__*/ React.createElement(AlertBoxContext.Provider, {
        value: {
            onRequestDismiss
        }
    }, /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        forwardedRef: ref,
        propKeysToRemove: propKeysToRemove,
        classNames: classNames,
        props: _object_spread_props(_object_spread({}, props), {
            role: 'alert'
        })
    }, skin === 'legal-warning' && warningImgUrl && /*#__PURE__*/ React.createElement("img", {
        src: warningImgUrl,
        alt: "warning"
    }), children));
});
/*
  Note: we shouldn't set the default value of dismissed to false here because that will make
  the component think it's a controlledDismissed variant and break the uncontrolled dismiss functionality
*/ AlertBox.defaultProps = {
    skin: 'standard'
};

export { AlertBox };
